import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referralService } from '../../services/referralService';
import { practitionerService } from '../../services/practitionerService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import tabs from both dashboards
import StatisticsCards from '../referral-management-dashboard/components/StatisticsCards';
import FilterPanel from '../referral-management-dashboard/components/FilterPanel';
import ReferralTable from '../referral-management-dashboard/components/ReferralTable';
import ReferralDetailModal from '../referral-management-dashboard/components/ReferralDetailModal';
import AISummaryModal from '../referral-management-dashboard/components/AISummaryModal';
import BrandingTab from '../practitioner-management-dashboard/components/BrandingTab';
import ContactTab from '../practitioner-management-dashboard/components/ContactTab';
import ReferralSettingsTab from '../practitioner-management-dashboard/components/ReferralSettingsTab';
import AnalyticsTab from '../practitioner-management-dashboard/components/AnalyticsTab';
import ContactsTab from '../practitioner-management-dashboard/components/ContactsTab';

import SecurityTab from './components/SecurityTab';


const PractitionerDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [activeTab, setActiveTab] = useState('referrals');
  const [practitioner, setPractitioner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAISummaryModal, setShowAISummaryModal] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    urgency: 'all',
    referringDentist: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'submissionDate',
    direction: 'desc'
  });

  // Load practitioner data and referrals
  useEffect(() => {
    if (!authLoading && userProfile) {
      if (!userProfile?.practitionerId) {
        setError('Aucun praticien associé à ce compte');
        setLoading(false);
        return;
      }
      loadData();
    }
  }, [authLoading, userProfile]);

  // Real-time subscription for new referrals
  useEffect(() => {
    if (!userProfile?.practitionerId) return;

    const channel = supabase?.channel('practitioner-referrals-changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'referrals',
          filter: `practitioner_id=eq.${userProfile?.practitionerId}`
        },
        (payload) => {
          const newReferral = {
            id: payload?.new?.id,
            practitionerId: payload?.new?.practitioner_id,
            patientFirstName: payload?.new?.patient_first_name,
            patientLastName: payload?.new?.patient_last_name,
            patientYearOfBirth: payload?.new?.patient_year_of_birth,
            patientSex: payload?.new?.patient_sex,
            patientPhone: payload?.new?.patient_phone,
            patientEmail: payload?.new?.patient_email,
            referringDentistName: payload?.new?.referring_dentist_name,
            referringPracticeName: payload?.new?.referring_practice_name,
            referringDentistEmail: payload?.new?.referring_dentist_email,
            referringDentistPhone: payload?.new?.referring_dentist_phone,
            toothNumbers: payload?.new?.tooth_numbers || [],
            referralReasons: payload?.new?.referral_reasons || [],
            clinicalNotes: payload?.new?.clinical_notes,
            imagingDownloadLink: payload?.new?.imaging_download_link,
            imagingPassword: payload?.new?.imaging_password,
            imagingCenter: payload?.new?.imaging_center,
            status: payload?.new?.status,
            urgency: payload?.new?.urgency,
            submissionDate: payload?.new?.submission_date,
            reviewedAt: payload?.new?.reviewed_at,
            scheduledAt: payload?.new?.scheduled_at,
            completedAt: payload?.new?.completed_at,
            notes: payload?.new?.notes,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          };

          setReferrals(prev => [newReferral, ...prev]);
          
          setStatistics(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              totalPending: prev?.totalPending + (newReferral?.status === 'new' || newReferral?.status === 'reviewed' ? 1 : 0),
              urgentCount: prev?.urgentCount + (newReferral?.urgency === 'urgent' || newReferral?.urgency === 'emergency' ? 1 : 0),
              byStatus: {
                ...prev?.byStatus,
                [newReferral?.status]: (prev?.byStatus?.[newReferral?.status] || 0) + 1
              }
            };
          });
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [userProfile?.practitionerId]);

  // Listen for referrals cleared event from SecurityTab
  useEffect(() => {
    const handleReferralsCleared = () => {
      loadData();
    };

    window.addEventListener('referrals-cleared', handleReferralsCleared);

    return () => {
      window.removeEventListener('referrals-cleared', handleReferralsCleared);
    };
  }, [userProfile?.practitionerId]);

  // Apply filters when data changes
  useEffect(() => {
    applyFiltersAndSort();
  }, [referrals, filters, sortConfig]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load practitioner details
      const practitionerData = await practitionerService?.getById(userProfile?.practitionerId);
      setPractitioner(practitionerData);

      // Load referrals for this practitioner only
      const referralsData = await referralService?.getAll(userProfile?.practitionerId);
      setReferrals(referralsData);

      // Load statistics for this practitioner
      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Refresh referral data without triggering loading state (prevents blinking)
  const handleRefreshReferral = async () => {
    try {
      // Silently reload referrals and statistics without showing loading spinner
      const referralsData = await referralService?.getAll(userProfile?.practitionerId);
      setReferrals(referralsData);

      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);

      // Update selectedReferral if modal is open
      if (selectedReferral) {
        const updatedReferral = referralsData?.find(r => r?.id === selectedReferral?.id);
        if (updatedReferral) {
          setSelectedReferral(updatedReferral);
        }
      }
    } catch (err) {
      console.error('Error refreshing referral data:', err);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...referrals];

    if (filters?.status && filters?.status !== 'all') {
      filtered = filtered?.filter(r => r?.status === filters?.status);
    }

    if (filters?.urgency && filters?.urgency !== 'all') {
      filtered = filtered?.filter(r => r?.urgency === filters?.urgency);
    }

    if (filters?.referringDentist) {
      filtered = filtered?.filter(r => 
        r?.referringDentistName?.toLowerCase()?.includes(filters?.referringDentist?.toLowerCase())
      );
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter(r => new Date(r?.submissionDate) >= new Date(filters?.dateFrom));
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(r => new Date(r?.submissionDate) <= new Date(filters?.dateTo));
    }

    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(r => 
        r?.patientFirstName?.toLowerCase()?.includes(term) ||
        r?.patientLastName?.toLowerCase()?.includes(term) ||
        r?.referringDentistName?.toLowerCase()?.includes(term)
      );
    }

    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.field];
      const bValue = b?.[sortConfig?.field];

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig?.direction === 'asc' ? comparison : -comparison;
    });

    setFilteredReferrals(filtered);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      urgency: 'all',
      referringDentist: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    });
  };

  const handleStatusUpdate = async (referralId, newStatus) => {
    try {
      await referralService?.updateStatus(referralId, newStatus);
      
      setReferrals(prev => prev?.map(r => 
        r?.id === referralId ? { ...r, status: newStatus } : r
      ));

      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);

      // Refresh selectedReferral if modal is open
      if (selectedReferral?.id === referralId) {
        const updatedReferral = await referralService?.getById(referralId);
        setSelectedReferral(updatedReferral);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleNotesUpdate = async (referralId, notes, internalNotesUpdatedAt) => {
    try {
      // The modal already called the service, so we just update local state
      // to keep everything in sync without duplicate database calls
      setReferrals(prev => prev?.map(r =>
        r?.id === referralId ? { ...r, internalNotes: notes, internalNotesUpdatedAt } : r
      ));
      
      // Update selectedReferral immediately to prevent modal reset
      setSelectedReferral(prev => 
        prev?.id === referralId ? { ...prev, internalNotes: notes, internalNotesUpdatedAt } : prev
      );
    } catch (err) {
      console.error('Error updating notes:', err);
      alert('Erreur lors de la mise à jour des notes');
    }
  };

  const handleDeleteReferral = async (referralId) => {
    try {
      if (!confirm('Êtes-vous sûr de vouloir supprimer cette référence ?')) {
        return;
      }
      
      await referralService?.delete(referralId);
      
      setReferrals(prev => prev?.filter(r => r?.id !== referralId));
      
      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);
    } catch (err) {
      console.error('Error deleting referral:', err);
      alert('Erreur lors de la suppression de la référence');
    }
  };

  const handleUrgencyUpdate = async (referralId, newUrgency) => {
    try {
      await referralService?.updateUrgency(referralId, newUrgency);
      
      setReferrals(prev => prev?.map(r => 
        r?.id === referralId ? { ...r, urgency: newUrgency } : r
      ));

      const stats = await referralService?.getStatistics(userProfile?.practitionerId);
      setStatistics(stats);
    } catch (err) {
      console.error('Error updating urgency:', err);
      alert('Erreur lors de la mise à jour de l\'urgence');
    }
  };

  const handleViewDetails = (referral) => {
    setSelectedReferral(referral);
    setShowDetailModal(true);
  };

  const handleViewAISummary = (referral) => {
    setSelectedReferral(referral);
    setShowAISummaryModal(true);
  };

  const handleGenerateReport = (referral) => {
    // Navigate to AI report generator with referral data
    navigate('/ai-report-generator', {
      state: {
        referralData: {
          referralId: referral?.id,
          patientFirstName: referral?.patientFirstName,
          patientLastName: referral?.patientLastName,
          patientYearOfBirth: referral?.patientYearOfBirth,
          patientPhone: referral?.patientPhone,
          patientEmail: referral?.patientEmail,
          referringDentistName: referral?.referringDentistName,
          referringPracticeName: referral?.referringPracticeName,
          referringDentistEmail: referral?.referringDentistEmail,
          referringDentistPhone: referral?.referringDentistPhone,
          toothNumbers: referral?.toothNumbers,
          referralReasons: referral?.referralReasons,
          clinicalNotes: referral?.clinicalNotes,
          urgency: referral?.urgency,
          diagnosis: referral?.referralReasons?.join(', ') || ''
        }
      }
    });
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSave = async (updates) => {
    try {
      setSaveSuccess(false);
      setError('');

      const updatedData = await practitionerService?.update(
        userProfile?.practitionerId,
        updates
      );
      
      setPractitioner(updatedData);
      setSaveSuccess(true);
      
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving practitioner data:', err);
      setError('Erreur lors de la sauvegarde');
    }
  };

  const handleNavigateToReports = () => {
    navigate('/practitioner-reports-dashboard');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleViewMyPage = () => {
    if (practitioner?.slug) {
      window.open(`/p/${practitioner?.slug}`, '_blank');
    }
  };

  const handleDelete = async (referralId) => {
    await handleDeleteReferral(referralId);
  };

  const tabs = [
    { id: 'referrals', label: 'Mes Références', icon: 'FileText' },
    { id: 'endo-reports', label: 'Comptes Rendus', icon: 'FileCheck', navigateTo: '/report-management-dashboard' },
    { id: 'reports', label: 'Rapports', icon: 'BarChart3' },
    { id: 'branding', label: 'Identité visuelle', icon: 'Palette' },
    { id: 'contact', label: 'Coordonnées', icon: 'Mail' },
    { id: 'contacts-directory', label: 'Répertoire Contacts', icon: 'Users' },
    { id: 'referral-settings', label: 'Paramètres', icon: 'Link' },
    { id: 'analytics', label: 'Statistiques', icon: 'Activity' },
    { id: 'security', label: 'Sécurité', icon: 'Shield' }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-secondary">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && !practitioner) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-2xl shadow-2xl border border-white/8 p-8 text-center">
          <div className="w-16 h-16 bg-error-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-error" />
          </div>
          <h2 className="text-xl font-semibold text-primary mb-2">Erreur d'accès</h2>
          <p className="text-muted mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2.5 bg-accent text-white rounded-xl hover:bg-accent/90 transition-all shadow-lg"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tableau de bord Praticien - EndoReferral</title>
      </Helmet>
      
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-white/8 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Personalized Logo */}
                {practitioner?.logoInitials ? (
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: practitioner?.logoBackgroundColor || '#4A90E2' }}
                  >
                    <span className="text-xl font-bold text-white">{practitioner?.logoInitials}</span>
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center shadow-lg">
                    <Icon name="User" size={24} className="text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-bold text-primary">{practitioner?.fullName}</h1>
                  <p className="text-sm text-muted">{practitioner?.practiceName}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* View My Page Button */}
                <Button
                  onClick={handleViewMyPage}
                  className="flex items-center gap-2 px-4 py-2.5 bg-accent text-white hover:bg-accent/90 rounded-xl transition-all shadow-lg hover:shadow-xl"
                >
                  <Icon name="ExternalLink" size={18} />
                  <span className="hidden sm:inline">Voir ma page</span>
                </Button>
                
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2.5 text-error hover:bg-error-bg rounded-xl transition-colors"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Success Message */}
          {saveSuccess && (
            <div className="mb-6 bg-success-bg border border-success/30 rounded-xl p-4 flex items-center gap-3 shadow-lg">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <p className="text-sm text-secondary">Modifications enregistrées avec succès</p>
            </div>
          )}

          {/* Error Message */}
          {error && practitioner && (
            <div className="mb-6 bg-error-bg border border-error/30 rounded-xl p-4 flex items-center gap-3 shadow-lg">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-sm text-secondary">{error}</p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 overflow-hidden">
            <div className="border-b border-white/8 bg-card-elevated">
              <nav className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => {
                      if (tab?.navigateTo) {
                        navigate(tab?.navigateTo);
                      } else {
                        setActiveTab(tab?.id);
                      }
                    }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                      activeTab === tab?.id
                        ? 'bg-accent text-white shadow-lg shadow-accent/30'
                        : 'text-secondary hover:text-primary hover:bg-white/5'
                    }`}
                  >
                    <Icon name={tab?.icon} size={20} />
                    <span className="font-medium">{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'referrals' && (
                <div className="space-y-6">
                  {/* Statistics Cards */}
                  {statistics && <StatisticsCards statistics={statistics} />}

                  {/* Filters */}
                  <FilterPanel
                    filters={filters}
                    onFilterChange={(field, value) => setFilters(prev => ({ ...prev, [field]: value }))}
                    onClearFilters={handleClearFilters}
                  />

                  {/* Referrals Table */}
                  <ReferralTable
                    referrals={filteredReferrals}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onViewDetails={handleViewDetails}
                    onViewAISummary={handleViewAISummary}
                    onStatusUpdate={handleStatusUpdate}
                    onDelete={handleDelete}
                    onUrgencyUpdate={handleUrgencyUpdate}
                    onGenerateReport={handleGenerateReport}
                  />
                </div>
              )}
              
              {activeTab === 'reports' && (
                <div className="text-center py-12">
                  <div className="max-w-2xl mx-auto">
                    <div className="w-20 h-20 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon name="BarChart3" size={40} className="text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold text-primary mb-3">
                      Rapports et Analyses Détaillées
                    </h3>
                    <p className="text-muted mb-8">
                      Accédez à vos rapports et analyses détaillées avec filtres avancés, visualisations interactives et exports CSV/PDF.
                    </p>
                    <Button
                      onClick={() => navigate('/practitioner-reports-dashboard')}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent to-primary text-white hover:shadow-xl rounded-xl transition-all mx-auto"
                    >
                      <Icon name="BarChart3" size={20} />
                      Ouvrir le Tableau de Bord des Rapports
                      <Icon name="ArrowRight" size={18} />
                    </Button>
                  </div>
                </div>
              )}
              
              {activeTab === 'endo-reports' && (
                <div className="text-center py-12">
                  <div className="max-w-2xl mx-auto">
                    <div className="w-20 h-20 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon name="FileCheck" size={40} className="text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold text-primary mb-3">
                      Comptes Rendus et Analyses Détaillées
                    </h3>
                    <p className="text-muted mb-8">
                      Accédez à vos comptes rendus et analyses détaillées avec filtres avancés, visualisations interactives et exports CSV/PDF.
                    </p>
                    <Button
                      onClick={() => navigate('/report-management-dashboard')}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent to-primary text-white hover:shadow-xl rounded-xl transition-all mx-auto"
                    >
                      <Icon name="FileCheck" size={20} />
                      Ouvrir le Tableau de Bord des Comptes Rendus
                      <Icon name="ArrowRight" size={18} />
                    </Button>
                  </div>
                </div>
              )}
              
              {activeTab === 'branding' && (
                <BrandingTab 
                  practitionerData={practitioner}
                  onSave={handleSave}
                />
              )}
              
              {activeTab === 'contact' && (
                <ContactTab 
                  practitionerData={practitioner}
                  onSave={handleSave}
                />
              )}
              
              {activeTab === 'contacts-directory' && (
                <ContactsTab 
                  practitionerId={userProfile?.practitionerId}
                />
              )}
              
              {activeTab === 'referral-settings' && (
                <ReferralSettingsTab 
                  practitionerData={practitioner}
                  onSave={handleSave}
                />
              )}
              
              {activeTab === 'analytics' && (
                <AnalyticsTab 
                  practitionerId={userProfile?.practitionerId}
                />
              )}
              
              {activeTab === 'security' && (
                <SecurityTab />
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Referral Detail Modal */}
      {showDetailModal && selectedReferral && (
        <ReferralDetailModal
          referral={selectedReferral}
          onClose={() => setShowDetailModal(false)}
          onStatusUpdate={handleStatusUpdate}
          onNotesUpdate={handleNotesUpdate}
          onRefresh={handleRefreshReferral}
        />
      )}

      {showAISummaryModal && selectedReferral && (
        <AISummaryModal
          referral={selectedReferral}
          onClose={() => setShowAISummaryModal(false)}
          onRefresh={handleRefreshReferral}
        />
      )}
    </>
  );
};

export default PractitionerDashboard;